import { cn } from "@/utils/theme";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData } from "@/workflow_api/types/workflow";
import { Icon } from "@elaniintc/goes-design-system";
import { VariableKeyBox } from "../key-box";
import { useMemo } from "react";
import { getOperationVerb } from "@/utils/format";

interface Props {
  id: string;
}

/**
 * Show an array with the output variable name
 * Used for 'read' operation
 * @param param0
 * @returns
 */
const ArrayInputOutputBlock = ({
  input,
}: {
  input: NodeData["inputs"][number];
}) => {
  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-[25px]",
          "bg-neutral-object-accent",
        )}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <div className="space-y-3">
            <VariableKeyBox
              name={input.value as string}
              type="array"
              key={`variable-${input.id}-array`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Show an array and a number with fixed names
 * Used for 'create', 'update', 'delete' operations
 * @param param0
 * @returns
 */
const ArrayAndNumberInputOutputBlock = ({
  operation,
  input,
}: {
  operation: string;
  input: NodeData["inputs"][number];
}) => {
  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-[25px]",
          "bg-neutral-object-accent",
        )}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <div className="space-y-3">
            <VariableKeyBox
              name={`ids-registros-${getOperationVerb(operation)}`}
              type="array"
              key={`variable-${input.id}-array`}
            />
            <VariableKeyBox
              name={`cantidad-registros-${getOperationVerb(operation)}`}
              type="number"
              key={`variable-${input.id}-count`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Content = ({ data }: { data: NodeData }) => {
  const operation = useMemo(() => {
    const operationInput = data.inputs.find(
      (input) => input.name === "operation",
    );
    return (operationInput?.value as string)?.toLowerCase();
  }, [data.inputs]);

  const outputInputElement = useMemo(() => {
    const outputNameInput = data.inputs.find(
      (input) => input.name === "output_name",
    );
    return outputNameInput;
  }, [data.inputs]);

  if (!data) {
    // loading
    return (
      <Icon
        name="spinner-third"
        className="animate-spin text-sm text-on-disabled"
        type="far"
      />
    );
  }

  if (operation === "read" && outputInputElement) {
    // For 'read' operation, return array type variable
    return (
      <ArrayInputOutputBlock
        input={outputInputElement}
        key={`input-${outputInputElement.name}-${outputInputElement.id}`}
      />
    );
  }

  if (
    (operation === "create" ||
      operation === "update" ||
      operation === "delete") &&
    outputInputElement
  ) {
    return (
      <ArrayAndNumberInputOutputBlock
        input={outputInputElement}
        operation={operation}
        key={`input-${outputInputElement.name}-${outputInputElement.id}`}
      />
    );
  }

  return <></>;
};

export const DataCenterVariables = ({ id }: Props) => {
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(id);
  if (!node) return <></>;
  const nodeData = node.data as unknown as NodeData;
  return <Content data={nodeData} />;
};
